import { useRef } from 'react';
import { X, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { useFocusTrap } from '../hooks/useFocusTrap';

/**
 * Reusable confirmation dialog with three variants (blue/amber/red).
 * Uses design tokens so the dialog re-skins with light/dark theme.
 *
 * Keyboard semantics handled by `useFocusTrap`:
 *   - Tab / Shift-Tab cycle inside the dialog only.
 *   - Escape triggers `onCancel`.
 *   - On open: focus moves to the first focusable element (Cancel
 *     button — by design, so a frantic Enter-mash doesn't confirm a
 *     destructive action).
 *   - On close: focus restored to whatever triggered the dialog.
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  onConfirm,
  onCancel,
  variant = 'blue',
  loading = false
}) {
  const dialogRef = useRef(null);
  useFocusTrap(dialogRef, open, onCancel);

  if (!open) return null;

  // Variants resolve to CSS-var-backed token classes (color-mix to
  // build a soft tint that works across both themes). The confirm
  // button stays solid for emphasis.
  const variants = {
    blue: {
      icon: Info,
      iconBg: 'bg-brand-soft',
      iconColor: 'text-brand',
      buttonBg: 'bg-brand hover:bg-[var(--color-brand-2)]',
    },
    amber: {
      icon: AlertTriangle,
      iconBg: 'bg-[color:var(--color-warn)]/15',
      iconColor: 'text-warn',
      buttonBg: 'bg-warn hover:opacity-90',
    },
    red: {
      icon: AlertCircle,
      iconBg: 'bg-[color:var(--color-err)]/15',
      iconColor: 'text-err',
      buttonBg: 'bg-err hover:opacity-90',
    },
  };

  const cfg = variants[variant] || variants.blue;
  const Icon = cfg.icon;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      <div
        ref={dialogRef}
        className="bg-surface rounded-2xl shadow-2xl border border-line w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-300"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-line flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${cfg.iconBg}`}>
              <Icon size={20} className={cfg.iconColor} aria-hidden="true" />
            </div>
            <h3 id="confirm-title" className="font-bold text-ink text-base tracking-tight">{title}</h3>
          </div>
          <button
            onClick={onCancel}
            disabled={loading}
            aria-label="Schließen"
            className="p-2 transition-all text-ink-3 hover:text-ink hover:bg-surface-2 rounded-full disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-ink-2 leading-relaxed mb-6">
            {message}
          </p>

          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={loading}
              className="flex-1 py-3 bg-surface-2 border border-line hover:bg-surface-2/60 text-ink-2 rounded-xl text-xs font-bold transition-all active:scale-95 disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className={`flex-1 py-3 ${cfg.buttonBg} text-white rounded-xl text-xs font-bold shadow-md transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center`}
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
